import { Inbox } from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { cn } from '@/lib/cn.js';

/**
 * État vide — `02` §0 : « message explicite et action suggérée (ex. « Aucune
 * règle transporteur pour cette boutique. Récupérer les profils Shopify »). »
 * `action` porte le bouton/lien suggéré ; l'appelant choisit son libellé exact
 * (spécifique à l'écran, jamais générique).
 *
 * Sans cadre par défaut : il vit dans le panneau de la liste ou de la section
 * qui l'accueille. `bordered` lui donne un contour pointillé quand il est posé
 * seul dans une page.
 */
export interface EmptyStateProps {
  title: string;
  description?: string | undefined;
  action?: ReactNode;
  icon?: ReactNode;
  bordered?: boolean;
  className?: string | undefined;
}

export function EmptyState({
  title,
  description,
  action,
  icon,
  bordered = false,
  className,
}: EmptyStateProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        bordered && 'rounded-lg border border-dashed border-border',
        className,
      )}
    >
      <div className="flex size-10 items-center justify-center rounded-lg bg-muted text-muted-foreground [&_svg]:size-5">
        {icon ?? <Inbox aria-hidden="true" />}
      </div>
      <div className="flex max-w-sm flex-col gap-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {action ? (
        <div className="mt-1 flex flex-wrap items-center justify-center gap-2">{action}</div>
      ) : null}
    </div>
  );
}
